import _ from 'lodash';
import { createModel } from '@rematch/core';
import { push } from 'connected-react-router';

import { RootModel } from '../index';
import { post, get } from 'src/api/api';
import { apiUrls, replaceUrls } from 'src/api/apiUrls';
import BetslipState, { Bet, PayloadBet } from '../../interfaces/BetslipState';
import { generateCalculationsPayload } from 'src/utils/lottoHelper';
import { betsToBetslipFormat, checkError } from 'src/utils/utils';

/**
 * This function returns the post payload for either calculate or submit.
 * @param rootState Redux's root state
 * @param type Whether we want the payload for calculate or submit endpoints
 */
const getPayload = (rootState, type: 'calculate' | 'submit' | 'place', placeBet=null) => {
  const { betslip: { bets, stakes, slip, singleSlip, accaSlip, systemSlip }, auth: { localWalletSelected } } = rootState;
  const selectedWallet = localWalletSelected ? localWalletSelected: null;

  let selectedSlip = singleSlip;
  if (slip === 1 && type !== 'place') selectedSlip = accaSlip;
  else if (slip === 2 && type !== 'place') selectedSlip = systemSlip;

  const currentBets = type === 'place' ? placeBet : bets;
  let totalStake = selectedSlip.stakeSelection === 'totalStake' ? selectedSlip.totalStake : 0;

  let stakeType = 1;

  if ((selectedSlip.stakeSelection === 'differentStake') && slip !== 2){
    stakeType = 3;
  } else if (selectedSlip.stakeSelection === 'sameStake' && type !== 'place'){
    stakeType = 2;
  } else if ((selectedSlip.stakeSelection === 'differentStake' || selectedSlip.stakeSelection === 'stakePerSystem')&& slip === 2){
    stakeType = 4;
  }

  const payload = {
    ...((selectedWallet && selectedWallet.id) && { customerWallet: selectedWallet.id }),
    betType:type !== 'place' ? slip + 1 : 1,
    divisor: rootState.betslip.divisor,
    ...(slip === 2 && { systems: selectedSlip.selectedCombinations }),
    stakeType,
    removeSuspendedMarkets: rootState.betslip.removeSuspendedMarkets,
    acceptOddsChanges: rootState.betslip.acceptOddsChanges,
    bets: _.map(currentBets, (b, i: number) => {
      if (type === 'place'){
        totalStake = b.stake * rootState.betslip.divisor;
      }
      const bet: PayloadBet = {
        ..._.pick(b, ['eventCode', 'marketCode', 'marketOutcomeCode']), // We only require these fields from our local bet.
        ...(selectedSlip.stakeSelection === 'differentStake' && slip === 0 && type !== 'place' && { stake: selectedSlip.stakes[i] }), // We only add stake to the bet when it's differentStake in single slip.
        ...(type === 'place' && { stake: +(b.stake) * rootState.betslip.divisor }),
        line: i + 1,
        isBanker: slip === 2 || (slip === 1 && accaSlip.stakeSelection === 'sameStake') ? !!selectedSlip.bankers[i] : false,
        ...(!_.isString(b.odds) && b.odds && { odds: b.odds }),
        productType: b.productType ? b.productType : 1
      };

      if (type !== 'calculate'){
        bet.jsonLabel = JSON.stringify(b.jsonLabel);
        bet.selectionType = b.selectionType || '';
      }

      if ((b.productType === 4 && b.selectedNumbers)) {
        const bankers = _.map(_.filter(b.selectedNumbers, { isBanker: true }), (banker) => banker.number) || [];
        const numbers = _.map(b.selectedNumbers, (number) => {
          if (number.type !== 'bonus'){
            return number.number;
          }
          return null;
        });
        const market = b.market;

        const payload = generateCalculationsPayload(
          _.compact(numbers),
          bankers,
          market,
          b.eventCode,
          0,
          market.specifiers[0].value,
          _.filter(b.selectedNumbers, { type: 'bonus' })
        );
        bet.prediction = payload.prediction;

      } else if (b.prediction && b.productType === 2){
        if (b.odds !== +b.odds || !b.odds){
          bet.prediction = {
            numbersToPredictCount: b.prediction.numbersToPredictCount,
            anyNumbers: b.prediction.selectedNumbers || b.prediction.anyNumbers,
            rankedNumbers:b.prediction.selectedBankers || b.prediction.rankedNumbers,
            inOrder: b.prediction.inOrder,
            ...(b.crossEvent && { type: b.crossEvent })
          };
        }
      }

      if (type === 'submit' || type === 'place') {
        if ('marketOutcomeCode' in bet) {
          bet.marketOutcomeCode = bet.marketOutcomeCode.toString();
        }

        if ('marketCode' in bet) {
          bet.marketCode = bet.marketCode.toString();
        }
      }
      if (type === 'place'){
        bet.stake === 100;
      }
      return bet;
    }),
    ...(selectedSlip.stakeSelection === 'sameStake' && { stake: selectedSlip.sameStake }),
    ...(selectedSlip.stakeSelection === 'totalStake' && { stake: totalStake })
  };

  if (slip === 2 && type !== 'place' &&(selectedSlip.stakeSelection === 'differentStake' || selectedSlip.stakeSelection === 'stakePerSystem')) {
    payload['stakes'] = _.reduce(stakes, (accum, s, i) => {
      // Fetch our stake values so it can be added to the stakes object.
      const value = selectedSlip.stakes[i];

      // If value is 0, then we don't add it.
      if (value === 0) return accum;

      if (_.isEmpty(accum)) {
        return { combinations: [{ lines: s.lines, stake: value }] };
      } else {
        return { combinations: [...accum['combinations'], { lines: s.lines, stake: value }] };
      }
    }, {});
  }

  return payload;
};

export default createModel<RootModel>()({
  state: {
    bets: [],
    stakes: [],
    singleSlip: {
      stakes: [], // Stores stake for each bet
      bankers: [],
      totalStake: 0,
      sameStake: 0,
      stakeSelection: 'totalStake'
    },
    accaSlip: {
      stakes: [], // Stores stake for each combination
      bankers: [],
      totalStake: 0,
      sameStake: 0,
      stakeSelection: 'totalStake'
    },
    systemSlip: {
      stakes: [], // Stores stake for each combination
      bankers: [],
      totalStake: 0,
      sameStake: 0,
      stakePerSystem: [],
      stakeSelection: 'totalStake',
      selectedCombinations: []
    },
    slip: 0,
    lastSlip: 0,
    betResult: null,
    registerBet: false,
    maximumProfit: 0,
    maximumReturn: 0,
    totalOdds:0,
    tax: { id: 0, taxType: '', taxPercentage: '' },
    potentialNetReturn:0,
    totalAmount: 0,
    processing: false,
    tempBets: {},
    divisor: 100,
    isGetTicket: false,
    showProfit: false,
    newBetIndex: 3,
    readShowPermutations: false,
    readOpenCombinations: [],
    readBetPermutations: null,
    showReadBetPermutation: false,
    acceptOddsChanges: false,
    removeSuspendedMarkets: false
  } as BetslipState,

  reducers: {
    setBets(state, { bets, stakes }: { bets: Bet | Bet[], stakes: BetslipState['singleSlip']['stakes'] }) {
      // If bets is an array, replace existing bets array.
      // Otherwise, add the bet to the existing array.

      if (_.isArray(bets)) {
        const differentElement = _.difference(bets, state.bets);
        let indexOfEvent = -1;
        if (differentElement.length > 0){
          indexOfEvent = _.findLastIndex(state.bets, { eventCode: differentElement[0].eventCode });
        }

        return {
          ...state,
          bets: _.sortBy(bets, 'eventCode'),
          singleSlip: { ...state.singleSlip, stakes },
          ...(bets.length > state.bets.length && { newBetIndex: indexOfEvent >  -1 ? indexOfEvent + 1 : bets.length -1 })
        };
      } else {
        const indexOfEvent = _.findLastIndex(state.bets, { eventCode: bets.eventCode });
        let newBets = JSON.parse(JSON.stringify(state.bets));
        let indexOfTournament = -1;

        if (indexOfEvent >= 0 && state.bets[indexOfEvent + 1]){
          (newBets).splice(indexOfEvent +1, 0, bets);
        } else {
          indexOfTournament = _.findLastIndex(state.bets, { tournamentName: bets.tournamentName });
          if (indexOfTournament >=0 && state.bets[indexOfTournament + 1]){
            (newBets).splice(indexOfTournament +1, 0, bets);
          } else {
            newBets = [...state.bets, bets];
          }
        }
        const differentElement = indexOfEvent > -1 ? indexOfEvent : indexOfTournament;

        return {
          ...state,
          bets: [ ...newBets],
          singleSlip: { ...state.singleSlip, stakes },
          newBetIndex: differentElement >  -1 ? differentElement + 1 : newBets.length -1
        };
      }
    },

    changeLastSlip(state, payload: BetslipState['lastSlip']){
      return {
        ...state,
        lastSlip: payload
      };
    },

    changeMultipleSystemRegister (state, payload: {
      systemStakes: BetslipState['stakes'],
      selectedCombinations: BetslipState['systemSlip']['selectedCombinations'],
      stakes: BetslipState['systemSlip']['stakes'],
      bankers: BetslipState['systemSlip']['bankers'],
      stakeSelection: BetslipState['systemSlip']['stakeSelection']}){

      return {
        ...state,
        stakes: payload.systemStakes,
        systemSlip: {
          ...state.systemSlip,
          ...(payload.bankers.length > 0 && { bankers: payload.bankers }),
          stakes: payload.stakes,
          selectedCombinations: payload.selectedCombinations,
          stakeSelection: payload.stakeSelection
        }
      };
    },


    setBetResult(state, payload: BetslipState['betResult']) {
      // If payload isn't null add it to the betResult.
      return {
        ...state,
        betResult: payload ? {
          betType: payload.betType,
          registerDate: payload.registerDate,
          ticketNumber: payload.ticketNumber,
          code: payload.code,
          maximumProfit: payload.maximumProfit,
          maximumReturn: payload.maximumReturn,
          totalStake: payload.totalStake,
          totalPotentialTax: payload.totalPotentialTax,
          potentialNetReturn: payload.potentialNetReturn,
          divisor: payload.divisor
        } : null,
        bets: payload ? _.map(payload.bets, b => {
          return {
            id: b.id,
            ticketCode: b.ticketCode,
            sportIcon: b.sportIcon,
            marketDescription: b.marketDescription,
            description: b.description,
            odds: b.odds,
            eventName: b.eventName,
            eventStart: b.eventStart,
            venue: b.venue,
            eventCode: b.eventCode,
            marketOutcomeCode: b.marketOutcomeCode,
            marketCode: b.marketCode,
            tournamentName: b.tournamentName,
            sportName: b.sportName,
            ...b
          };
        }) : [],
        stakes: payload ? payload.stakes.combinations : []
      };
    },

    setSlip(state, payload: number) {
      return { ...state, slip: payload };
    },

    setGetTicket(state, payload:boolean){
      return { ...state, isGetTicket: payload };
    },

    updateReadOpenCombinations(state, payload: number[]) {
      return { ...state, readOpenCombinations: payload };
    },

    clearState(state) {
      return {
        ...state,
        bets: [],
        stakes: [],
        singleSlip: {
          ...state.singleSlip,
          stakes: [],
          bankers: [],
          totalStake: 0,
          sameStake: 0
        },
        accaSlip: {
          stakes: [],
          bankers: [],
          totalStake: 0,
          sameStake: 0,
          stakeSelection: 'totalStake'
        },
        systemSlip: {
          stakes: [],
          bankers: [],
          totalStake: 0,
          sameStake: 0,
          stakePerSystem: [],
          stakeSelection: 'totalStake',
          selectedCombinations: []
        },
        slip: 0,
        registerBet: false,
        lastSlip: state.slip,
        betResult: null,
        maximumProfit: 0,
        totalAmount: 0,
        tax: { id: 0, taxType: '', taxPercentage: '' },
        potentialNetReturn:0,
        maximumReturn: 0,
        tempBets: state.tempBets,
        totalOdds: 0,
        processing: false,
        divisor: 100,
        isGetTicket: false,
        readShowPermutations: false,
        readOpenCombinations: [],
        readBetPermutations: null,
        showReadBetPermutation: false
      };
    },

    setTempBets(state) {
      return {
        ...state,
        tempBets: state
      };
    },

    setStakeSelection(state, payload: 'totalStake' | 'sameStake' | 'differentStake' | 'stakePerSystem') {
      let selectedSlip = 'singleSlip';
      if (state.slip === 1) selectedSlip = 'accaSlip';
      else if (state.slip === 2) selectedSlip = 'systemSlip';

      return {
        ...state,
        [selectedSlip]: {
          ...state[selectedSlip],
          stakeSelection: payload
        }
      };
    },

    setSingleStake(state, payload: { key?: string, index?: number, value: number }) {
      const { key, index, value } = payload;

      if (!_.isUndefined(index)) {
        // If there's an index, update value of stakes array in that position.
        const newStakes = [...state.singleSlip.stakes];
        newStakes[index] = value * state.divisor;

        return {
          ...state,
          singleSlip: {
            ...state.singleSlip,
            stakes: newStakes
          }
        };
      } else {
        // Otherwise, update the value of the given key.
        return {
          ...state,
          singleSlip: {
            ...state.singleSlip,
            [key || state.singleSlip.stakeSelection]: value * state.divisor
          }
        };
      }
    },

    setAccaStake(state, payload: { key?: string, value: number }) {
      const { key, value } = payload;

      // Update the value of the given key.
      return {
        ...state,
        accaSlip: {
          ...state.accaSlip,
          [key || state.accaSlip.stakeSelection]: value * state.divisor
        }
      };
    },

    setShowReadPermutations(state, payload: boolean){
      return { ...state, readShowPermutations: payload };
    },

    setSystemPerSystem(state, payload: BetslipState['systemSlip']['stakePerSystem']){
      return {
        ...state,
        systemSlip: {
          ...state.systemSlip,
          stakePerSystem: payload
        }
      };
    },

    setSystemStake(state, payload: { key?: string, index?: number, value: number }) {
      const { key, index, value } = payload;

      if (!_.isUndefined(index) || _.isArray(payload)) {
        // If there's an index, update value of stakes array in that position.
        let newStakes = [...state.systemSlip.stakes];
        let updateKey = 'stakes';
        if (_.isArray(payload)) {
          for (let index = 0; index < payload.length; index++) {
            newStakes[payload[index].index] = payload[index].value;
          }
        } else {
          if (state.systemSlip.stakeSelection === 'stakePerSystem'){
            newStakes = _.isArray(state.systemSlip.stakePerSystem) ?
              [...state.systemSlip.stakePerSystem] :
              [];
            updateKey = 'stakePerSystem';
          }
          newStakes[index] = value * state.divisor;
        }

        return {
          ...state,
          systemSlip: {
            ...state.systemSlip,
            [updateKey]: newStakes
          }
        };
      } else {
        // Otherwise, update the value of the given key.
        return {
          ...state,
          systemSlip: {
            ...state.systemSlip,
            [key || state.systemSlip.stakeSelection]: value * state.divisor
          }
        };
      }
    },

    setAllStakes(state, payload: BetslipState['systemSlip']['stakes']){
      return {
        ...state,
        systemSlip: {
          ...state.systemSlip,
          stakes: payload
        }
      };
    },

    applySlipState(state: BetslipState): BetslipState {
      return { ...state, ...state.tempBets };
    },

    resetSystemStakes(state){
      return {
        ...state,
        systemSlip: {
          ...state.systemSlip,
          stakes: []
        }
      };
    },

    setOutcomesState(state, payload:any[]) {
      const bets = JSON.parse(JSON.stringify(state.bets));
      for (let i=0; i<payload.length; i+=1){
        let oddsChanged = null;
        const betIndex = _.findIndex(bets, { marketOutcomeCode: payload[i].outcomes.id });
        if (payload[i].outcomes.odds > bets[betIndex].odds) {
          oddsChanged = 'increased';
        } else if (payload[i].outcomes.odds < bets[betIndex].odds){
          oddsChanged = 'decreased';
        }

        bets[betIndex] = {
          ...bets[betIndex],
          odds: payload[i].outcomes.odds,
          marketStatusName: payload[i].markets.status,
          marketStatusNumber: payload[i].markets.statusNumber,
          outcomeStatusNumber:payload[i].outcomes.statusNumber,
          outcomeStatusName: payload[i].outcomes.status,
          oddsChanged
        };
      }
      return { ...state, bets: bets };
    },

    setMultipleSystemStakes(state, payload: { index:number, value:number }[]){
      const newStakes = (JSON.parse(JSON.stringify(state.systemSlip.stakes)));

      for (let index = 0; index < payload.length; index++) {
        newStakes[payload[index].index] = payload[index].value * state.divisor;
      }

      return {
        ...state,
        systemSlip: {
          ...state.systemSlip,
          stakes: newStakes
        }
      };
    },

    setMultipleSingleStakes(state, payload: number[]){
      return {
        ...state,
        singleSlip: {
          ...state.singleSlip,
          stakes: payload
        }
      };
    },

    updateReadBetPermutations(state, payload: { showPermutations: boolean, permutations: BetslipState['readBetPermutations']}){
      return { ...state, showReadBetPermutation: payload.showPermutations, readBetPermutations: payload.permutations };
    },

    setProcessing(state, payload: boolean) {
      return { ...state, processing: payload };
    },

    clearTemporaryBets(state) {
      return { ...state, tempBets: {} };
    },

    changeSelectedCombinations(state, payload: number[]){
      return { ...state, systemSlip:{ ...state.systemSlip, selectedCombinations: payload } };
    },

    setCalculationResults(
      state,
      payload: {
        stakes?: {
          combinations: BetslipState['stakes']
          bankersCount?: number
          outcomesCount?: number,
          systemMaximum?: number,
          systemMinimum?: number
       },
        bets?: BetslipState['bets'],
        maximumProfit?: BetslipState['maximumProfit'],
        maximumReturn?: BetslipState['maximumReturn']
        combinationsStakes: BetslipState['systemSlip']['stakes'],
        totalOdds?: BetslipState['totalOdds'],
        totalStake?:BetslipState['totalAmount'],
        divisor?: number
      }
    ) {
      const divisor = payload.divisor || state.divisor;

      return {
        ...state,
        ...(payload.stakes && { stakes: payload.stakes.combinations }), // If there's stakes, add their combinations.
        ...(payload.bets && { bets: _.map(state.bets, (b, i) => ({ ...b, combinationExclusions: payload.bets[i].combinationExclusions })) }), // If there's bets, add missing combinationExclusions.
        ...(state.slip === 1 && { accaSlip: { ...state.accaSlip, stakes: payload.combinationsStakes } }), // If it's acca, add combinations to accaSlip.
        ...(state.slip === 2 && { systemSlip: { ...state.systemSlip, stakes: payload.combinationsStakes } }), // If it's system, add combinations to systemSlip.
        maximumProfit: (payload.maximumProfit / divisor) || 0,
        maximumReturn: (payload.maximumReturn / divisor) || 0,
        ...(state.slip === 2 && { systemSlip: { ...state.systemSlip, stakes: payload.combinationsStakes,
          bankersCount: payload.stakes.bankersCount,
          outcomesCount: payload.stakes.outcomesCount,
          systemMaximum: payload.stakes.systemMaximum,
          systemMinimum: payload.stakes.systemMinimum
        } }),
        totalOdds: payload.totalOdds || 0,
        totalAmount: payload.totalStake / divisor || 0
      };
    },

    setTaxes(state, payload: { tax: BetslipState['tax'], potentialNetReturn: BetslipState['potentialNetReturn']}){
      return { ...state, tax:payload.tax, potentialNetReturn: payload.potentialNetReturn };
    },

    setBankers(state, payload: { index: number, isBanker: boolean, everySlip?: boolean }) {
      const { index, isBanker, everySlip } = payload;

      if (!everySlip) {
        const bet = state.bets[index];
        if (!bet) return state;

        const indexes = _.keys(_.pickBy(state.bets, { eventCode: bet.eventCode }));
        let selectedSlip = 'singleSlip';
        if (state.slip === 1) selectedSlip = 'accaSlip';
        else if (state.slip === 2) selectedSlip = 'systemSlip';

        const bankers = state[selectedSlip].bankers;

        for (const banker of indexes){
          bankers[+banker] = isBanker;
        }

        return {
          ...state,
          [selectedSlip]: {
            ...state[selectedSlip],
            bankers
          }
        };
      } else {
        // Remove from every banker array
        const singleBankers = [...state.singleSlip.bankers];
        const accaBankers = [...state.accaSlip.bankers];
        const systemBankers = [...state.systemSlip.bankers];
        _.pullAt(singleBankers, index);
        _.pullAt(accaBankers, index);
        _.pullAt(systemBankers, index);

        return {
          ...state,
          singleSlip: {
            ...state.singleSlip,
            bankers: singleBankers
          },
          accaSlip: {
            ...state.accaSlip,
            bankers: accaBankers
          },
          systemSlip: {
            ...state.systemSlip,
            bankers: systemBankers
          }
        };
      }
    },

    setShowProfit(state) {
      return { ...state, showProfit: !state.showProfit };
    },

    setBetslipOptions(state, payload: string){
      return { ...state, [payload]: !state[payload] };
    },

    setRegisterBet(state, payload:boolean){
      return { ...state, registerBet: payload };
    }
  },

  effects: (dispatch) => ({
    /**
     * Function used to add a bet to our model (but only if that bet doesn't exist yet).
     * If it's the first bet, it will also initialize sameStake & totalStake values for every slip.
     *
     * It calls calculateBets() once it's done.
     * @param payload Expects a payload of type Bet.
     */
    async addBet(payload: Bet, rootState): Promise<void> {
      dispatch.betslip.setProcessing(true);

      if (rootState.betslip.betResult !== null)
        await dispatch.betslip.setBetResult(null);

      // In case of quick bet, or other situation where we want to preserve the stake,
      // we need to remove it from the bet and add it to the stakes.
      const customStake = payload.stake;
      delete payload.stake;

      const bets = _.isNull(rootState.betslip.betResult) ? [...rootState.betslip.bets] : [];
      const index = _.findIndex(bets, payload);

      if (index === -1 || payload.type === 'numbers') {

        // We're in single slip, add default stake to the stakes array so it matches the newly added bet.
        let stakeValue = rootState.content.stakingRules.sports.single.default;
        if (customStake) {
          stakeValue = customStake;
        } else if (payload.type === 'racing') {
          stakeValue = rootState.content.stakingRules.racing.fixedOdds.default;
        } else if (payload.type === 'numbers'){
          stakeValue = rootState.content.stakingRules.lotto.single.default;
        }

        const stakes = [
          ...rootState.betslip.singleSlip.stakes,
          stakeValue * rootState.betslip.divisor
        ];

        if (bets.length === 0) {
          // Initialize betslip values for:
          // single
          dispatch.betslip.setSingleStake({ key: 'sameStake', value: rootState.content.stakingRules.sports.single.default });
          dispatch.betslip.setSingleStake({ key: 'totalStake', value: rootState.content.stakingRules.sports.single.default });
          // acca
          dispatch.betslip.setAccaStake({ key: 'sameStake', value: rootState.content.stakingRules.sports.acca.default });
          dispatch.betslip.setAccaStake({ key: 'totalStake', value: rootState.content.stakingRules.sports.acca.default });
          // system
          dispatch.betslip.setSystemStake({ key: 'sameStake', value: rootState.content.stakingRules.sports.system.default });
          dispatch.betslip.setSystemStake({ key: 'stakePerSystem', value: rootState.content.stakingRules.sports.system.default });
          dispatch.betslip.setSystemStake({ key: 'totalStake', value: rootState.content.stakingRules.sports.system.default });
        }

        await dispatch.betslip.setBets({ bets: payload, stakes });

        // If we have a banker from same event, we want this event to also become a banker.
        const bankers = rootState.betslip.systemSlip.bankers;
        if (bankers?.length > 0){
          for (const bankerIndex in bankers){
            if (bankers[bankerIndex] && rootState.betslip.bets[bankerIndex].eventCode === payload.eventCode) {
              dispatch.betslip.updateBankerAtIndex({ index: rootState.betslip.bets.length, isBanker: true });
            }
          }
        }

        dispatch.betslip.calculateBets(false);
      } else {
        dispatch.betslip.setProcessing(false);
      }
    },

    changeStakePerSystem(payload: number[]){
      dispatch.betslip.setSystemPerSystem(payload);
    },

    /**
     * Function used to add multiple bets to our model at once (but only if those bets don't exist yet).
     * If the bets array is empty, it will also initialize sameStake & totalStake values for every slip.
     *
     * It calls calculateBets() once it's done.
     * @param payload Expects a payload of type Bet.
     */
    async addBets(payload: Bet[], rootState): Promise<void> {
      dispatch.betslip.setProcessing(true);

      if (rootState.betslip.betResult !== null)
        await dispatch.betslip.setBetResult(null);

      const bets = [...rootState.betslip.bets];
      let newBets: Bet[] = new Array();
      let stakes: number[] = new Array();

      for (const b of payload) {

        const customStake = b.stake;
        delete b.stake;
        const index = _.findIndex(bets, b);

        if (index === -1) {
          let stakeValue = rootState.content.stakingRules.sports.single.default;
          if (customStake){
            stakeValue = customStake;
          } else if (b.type === 'racing') {
            stakeValue = rootState.content.stakingRules.racing.fixedOdds.default;
          } else if (b.type=== 'numbers'){
            stakeValue = rootState.content.stakingRules.lotto.single.default;
          }

          stakes = [
            ...rootState.betslip.singleSlip.stakes,
            ...stakes,
            stakeValue * rootState.betslip.divisor
          ];

          if (bets.length === 0) {
            // Initialize betslip values for:
            // single
            dispatch.betslip.setSingleStake({ key: 'sameStake', value: rootState.content.stakingRules.sports.single.default });
            dispatch.betslip.setSingleStake({ key: 'totalStake', value: rootState.content.stakingRules.sports.single.default });
            // acca
            dispatch.betslip.setAccaStake({ key: 'sameStake', value: rootState.content.stakingRules.sports.acca.default });
            dispatch.betslip.setAccaStake({ key: 'totalStake', value: rootState.content.stakingRules.sports.acca.default });
            // system
            dispatch.betslip.setSystemStake({ key: 'sameStake', value: rootState.content.stakingRules.sports.system.default });
            dispatch.betslip.setSystemStake({ key: 'stakePerSystem', index: 0, value: rootState.content.stakingRules.sports.system.default });
            dispatch.betslip.setSystemStake({ key: 'totalStake', value: rootState.content.stakingRules.sports.system.default });
          } else {
            newBets = bets;
          }

          newBets.push(b);
        }
      }

      if (newBets.length > 0) {
        await dispatch.betslip.setBets({ bets: newBets, stakes });

        dispatch.betslip.calculateBets(false);
      } else {
        dispatch.betslip.setProcessing(false);
      }
    },

    /**
     * Function used to clear bet confirmation results.
     */
    clearBetResult(): void {
      dispatch.betslip.setBetResult();
    },

    /**
     * Function used to change the stored selected slip.
     *
     * It calls calculateBets() once it's done.
     * @param payload Expects the slip number.
     */
    changeSlip(payload: number): void {
      dispatch.betslip.setProcessing(true);

      dispatch.betslip.setSlip(payload);
      dispatch.betslip.changeLastSlip(payload);

      dispatch.betslip.calculateBets(false);
    },

    /**
     * Function used to reset the betslip.
     */
    clearBets(): void {
      dispatch.betslip.clearState();
    },
    /**
     * Function used to change the stored staking selection, for the currently selected slip.
     *
     * It calls calculateBets() once it's done.
     * @param payload Expects the staking value out of the three available.
     */
    changeStakeSelection(payload: 'totalStake' | 'sameStake' | 'differentStake' | 'stakePerSystem'): void {
      dispatch.betslip.setProcessing(true);

      dispatch.betslip.setStakeSelection(payload);

      dispatch.betslip.calculateBets(false);
    },

    /**
     * Function used to update a slip stake value. Based on the currently selected slip.
     *
     * It calls calculateBets() once it's done.
     * @param payload Expects a payload with a mandatory value and optional key/index params. This is used to identify where to place the value.
     */
    async updateBetStake(payload: { key?: string, index?: number, value: number }, rootState): Promise<void> {
      dispatch.betslip.setProcessing(true);

      if (rootState.betslip.slip === 0) {
        await dispatch.betslip.setSingleStake(payload);
      } else if (rootState.betslip.slip === 1) {
        await dispatch.betslip.setAccaStake(payload);
      } else if (rootState.betslip.slip === 2) {
        await dispatch.betslip.setSystemStake(payload);
      }

      dispatch.betslip.calculateBets(false);
    },

    async updateMultipleSystemStakes(payload: {index: number, value:number}[], rootState): Promise<void> {
      dispatch.betslip.setProcessing(true);
      if (rootState.betslip.slip === 2){
        dispatch.betslip.resetSystemStakes();
        dispatch.betslip.setMultipleSystemStakes(payload);
        dispatch.betslip.calculateBets(false);
      }
    },

    async updateMultipleSingleStakes(payload: number[]){
      dispatch.betslip.setProcessing(true);
      dispatch.betslip.setMultipleSingleStakes(payload);
    },

    async updateAllSystemStakes(payload:BetslipState['systemSlip']['stakes']){
      dispatch.betslip.setProcessing(true);
      await dispatch.betslip.setAllStakes(payload);
      await dispatch.betslip.calculateBets(true);
    },

    /**
     * Function used to remove a bet from the bets array, through a specified index.
     *
     * It calls calculateBets() once it's done.
     * @param payload Expects index number.
     */
    async removeBetAtIndex(payload: number, rootState): Promise<void> {
      dispatch.betslip.setProcessing(true);

      const lineNumber = payload + 1;

      // Removes both bet and stake at the given index.
      const bets = [...rootState.betslip.bets];
      const stakes = [...rootState.betslip.singleSlip.stakes];
      _.pullAt(bets, payload);
      _.pullAt(stakes, payload);

      // If acca or system slip, we need to also remove the stakes.
      if (rootState.betslip.slip !== 0) {
        let combinationStakes = [...rootState.betslip.stakes];
        // Get indexes of every combination whose line matches the line removed.
        const indexes = _.map(combinationStakes, (s, i) => _.includes(s.lines, lineNumber) ? i : null);

        combinationStakes = _.compact(_.map(combinationStakes, (s, i) => {
          // If this combination is in the indexes array, remove it.
          if (indexes.indexOf(i) !== -1) return null;

          // Go through all the lines of this combination. If the number is after the removed line, decreased it by 1.
          // We do this so that the lines stay correct to avoid losing stakes on calculation.
          // i.e.: if we had lines 1-4 and removed line 3, line 4 would now be line 3, but we still want to keep 4's stake.
          s.lines = [...new Set(_.map(s.lines, l => l > lineNumber ? l-1 : l))];
          return s;
        }));

        // Update combinations before calculating
        await dispatch.betslip.setCalculationResults({
          stakes: { combinations: combinationStakes },
          combinationsStakes: _.map(combinationStakes, s => s.stake)
        });
      }

      // If there are still bets left, update it.
      // Otherwise, reset the betslip.
      if (bets.length > 0) {
        dispatch.betslip.setBets({ bets, stakes });
        dispatch.betslip.setBankers({ index: payload, isBanker: false, everySlip: true });

        dispatch.betslip.calculateBets(true);
      } else {
        dispatch.betslip.clearState();

        dispatch.betslip.setProcessing(false);
      }
    },

    /**
     * Function used to update a bet's banker status from the bets array, through a specified index.
     *
     * It calls calculateBets() once it's done.
     * @param payload Expects a payload with index and isBanker flag.
     */
    async updateBankerAtIndex(payload: { index: number, isBanker: boolean }): Promise<void> {
      dispatch.betslip.setProcessing(true);

      dispatch.betslip.setBankers(payload);

      dispatch.betslip.calculateBets(true);
    },

    async updateBankerByBets(payload: Bet[], rootState): Promise<void>{
      dispatch.betslip.setProcessing(true);
      const { bets } = rootState.betslip;

      for (const bet of payload){
        const betIndex = _.findIndex(bets, bet);

        dispatch.betslip.setBankers({ index: betIndex, isBanker: true });
      }

      dispatch.betslip.calculateBets(true);
    },

    /**
     * Function used to trigger betslip calculations.
     */
    async calculateBets(resetStakes: boolean, rootState): Promise<void> {
      try {
        dispatch.betslip.setProcessing(true);

        const { betslip: { slip, accaSlip } } = rootState;
        const payload = getPayload(rootState, 'calculate');

        const response = await post(apiUrls.calculateBets, payload);
        if (response && response.status === 200) {
          const { stakes } = response.data.results;
          let combinationsStakes = [];

          // If we're in acca or system, we need to store the combinations
          // since it's used for staking.
          if (stakes && slip !== 0) {
            if (!resetStakes) {
              if (slip === 1) combinationsStakes = accaSlip.stakes;
              else if (slip === 2) {
                const totalStake = (+rootState.betslip.systemSlip.totalStake / rootState.betslip.divisor).toFixed(2);

                if (+totalStake !== +(response.data.results.totalStake / rootState.betslip.divisor).toFixed(2)){
                  dispatch.betslip.setSystemStake({
                    key: 'totalStake', value: +(response.data.results.totalStake / rootState.betslip.divisor).toFixed(2)
                  });
                }
                const newCombinations = [];
                for (const line of response.data.results.stakes.combinations){
                  newCombinations.push(line.stake);
                }
                combinationsStakes = newCombinations;
              }
            }

            // Add default stakes to new combinations
            const combinationsArr = _.map(stakes.combinations, c => c.stake);
            if (combinationsStakes.length !== combinationsArr.length) {
              for (let i = 0; i < combinationsArr.length; i++) {
                if (!combinationsStakes[i]) {
                  combinationsStakes.push(rootState.content.stakingRules.sports[slip === 1 ? 'acca' : 'system'].default * rootState.betslip.divisor);
                }
              }
            }
          }
          dispatch.betslip.setCalculationResults({ ...response.data.results, combinationsStakes });
          dispatch.betslip.setTaxes({ tax: response.data.results.tax, potentialNetReturn: response.data.results.potentialNetReturn });
          dispatch.betslip.setProcessing(false);
        } else {
          dispatch.betslip.setProcessing(false);
        }
      } catch (error: any) {
        dispatch.betslip.setProcessing(false);
        dispatch.toast.showToast({ type: 'error', message: (error.response ? error.response.data?.message : '') });
      }
    },

    /**
     * Function used to trigger betslip submission.
     * If successfully, resets the betslip and stores the betslip result to be used in bet confirmation.
     */

    async submitBets(isRetail: boolean, rootState): Promise<void | { redirect: string }> {
      try {
        dispatch.betslip.setProcessing(true);
        dispatch.betslip.setTempBets();
        let printerStatus = isRetail ? await dispatch.peripherals.checkValidPrinter(): { status: 200 };

        if (!isRetail && !rootState.auth.authenticated){
          printerStatus = { status: 400 };
          dispatch.toast.showToast({ type: 'error', message: 'Please login again' });
        }

        if (printerStatus.status === 200){

          const payload = getPayload(rootState, 'submit');

          const response = await post(apiUrls.submitBets, payload);
          if (response) {
            if (response.status === 200) {
              dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });

              dispatch.betslip.changeLastSlip(rootState.betslip.slip);
              await dispatch.betslip.clearBets();

              dispatch.betslip.setProcessing(false);
              if (!isRetail) await dispatch.auth.checkUser();

              if (isRetail) {
                dispatch.peripherals.submitToPrinter(response.data.results);
                dispatch.peripherals.setShowReceipt(false);
                await dispatch.peripherals.getTillTransactions();
                await dispatch.peripherals.getTillBalance();
              } else {
              // We don't need to show bet confirmation to the customer since it will be printed.
                dispatch.betslip.setBetResult(response.data.results);
              }
            } else if (response.status === 409) {
              const message = checkError(response.data.results);
              dispatch.toast.showToast({ type: 'error', message });
              dispatch.betslip.setProcessing(false);

            // TODO: remove comment when backend sends different error codes.
            // return { redirect: '/account/deposit' };
            } else if (response.status === 500){
              const message = response.data.results.message;
              dispatch.toast.showToast({ type: 'error', message });
              dispatch(push('/account/deposit'));
              dispatch.betslip.setProcessing(false);
            } else {
              const message = checkError(response.data.results);
              dispatch.toast.showToast({ type: 'error', message });
              dispatch.betslip.setProcessing(false);
            }
          } else {
            const message = checkError(response.data.results);
            dispatch.toast.showToast({ type: 'error', message });
            dispatch.betslip.setProcessing(false);
          }
        }
      } catch (error: any) {
        // const message = checkError(error.data);
        // dispatch.toast.showToast({ type: 'error', message });
        dispatch.betslip.setProcessing(false);
      }
    },

    /**
     * Function used to trigger betslip submission.
     * If successfully, resets the betslip and stores the betslip result to be used in bet confirmation.
     */

    async placeBet({ isRetail, bet }, rootState): Promise<void | { redirect: string }> {
      try {
        dispatch.betslip.setProcessing(true);
        const payload = getPayload(rootState, 'place', bet);

        const response = await post(apiUrls.submitBets, payload);
        if (response) {
          if (response.status === 200) {
            dispatch.toast.showToast({ type: 'success', message: 'Bet accepted, view details in history' });

            dispatch.betslip.setProcessing(false);
            if (!isRetail) dispatch.auth.getCustomer();

            if (isRetail) {
              dispatch.peripherals.submitToPrinter(response.data.results);
              await dispatch.peripherals.getTillTransactions();
              await dispatch.peripherals.getTillBalance();
            }
          } else if (response.status === 409) {
            const message = checkError(response.data.results);
            dispatch.toast.showToast({ type: 'error', message });
            dispatch.betslip.setProcessing(false);

            // TODO: remove comment when backend sends different error codes.
            // return { redirect: '/account/deposit' };
          } else {
            const message = checkError(response.data.results);
            dispatch.toast.showToast({ type: 'error', message });
            dispatch.betslip.setProcessing(false);
          }
        }
      } catch (error: any) {
        dispatch.toast.showToast({ type: 'error', message: (error.response && error.response.data.message) });
        dispatch.betslip.setProcessing(false);
      }
    },

    async registerBet(payload, rootState): Promise<void | { redirect: string }> {
      try {
        dispatch.betslip.setProcessing(true);

        const payload = getPayload(rootState, 'submit');

        const response = await post(apiUrls.registerBet, payload);
        if (response && response.status === 200) {
          dispatch.betslip.setProcessing(false);

          if (rootState.auth.tillUser){
            dispatch.peripherals.printRegister(response.data.results);
          }
          return  response.data.results.id;
        }
        const message = checkError(response.data.results);
        dispatch.toast.showToast({ type: 'error', message });
        dispatch.betslip.setProcessing(false);
        return null;
      } catch (error: any) {
        dispatch.toast.showToast({ type: 'error', message: (error.response && error.response.data.message) });
        dispatch.betslip.setProcessing(false);
        return null;
      }
    },


    async removeMultipleBets(payload, rootState):  Promise<void> {

      // This function is used to remove multiple events bet on the special markets for lotto.
      const bets = [...rootState.betslip.bets];
      const stakes = [...rootState.betslip.singleSlip.stakes];
      _.pullAt(bets, payload);
      _.pullAt(stakes, payload);
      if (bets.length > 0) {
        dispatch.betslip.setBets({ bets, stakes });

        dispatch.betslip.calculateBets(false);
      } else {
        dispatch.betslip.clearState();

        dispatch.betslip.setProcessing(false);
      }
    },

    async addSelectedCombinations(payload: number, rootState): Promise<void> {
      const selectedCombinations = rootState.betslip.systemSlip.selectedCombinations;
      selectedCombinations.push(payload);
      dispatch.betslip.changeSelectedCombinations(_.uniq(selectedCombinations));
    },

    clearTempBets(): void {
      dispatch.betslip.clearTemporaryBets();
    },

    async addMultipleSelectedCombinations(payload: number[]): Promise<void> {
      dispatch.betslip.changeSelectedCombinations(payload);
    },

    async removeSelectedCombination(payload, rootState): Promise<void> {
      const selectedCombinations = rootState.betslip.systemSlip.selectedCombinations;
      const removeAtIndex = _.filter(selectedCombinations, (selected) => selected !== payload);
      dispatch.betslip.changeSelectedCombinations(removeAtIndex);
    },

    async resetSelectedCombination(): Promise<void> {
      dispatch.betslip.changeSelectedCombinations([]);
    },

    changeStake(): void {},

    submitQuickBets(): void {},

    getPossiblePermutations(): void {},

    checkOutcomes(payload: string){
      const response = JSON.parse(payload);
      dispatch.betslip.setOutcomesState(response);
    },

    changeRegisterBet(payload):void {
      dispatch.betslip.setRegisterBet(payload);
    },

    async operatBookBetToBetslip(payload : {
      stakes: any,
      betType: number,
      stakeType: number,
      totalStake: number,
      stake: number,
      bets: [],
      systems: number[]
    }) {
      try {
        const betslip = JSON.parse(JSON.stringify(payload));

        dispatch.betslip.changeSlip(betslip.betType -1);

        const newStakeCombinations: BetslipState['systemSlip']['stakes'] = betslip.stakes.combinations;

        const bets:any = betsToBetslipFormat(betslip.bets);

        await dispatch.betslip.addBets(bets);

        if (betslip.betType === 1){
          let stakeSelection : 'totalStake' | 'differentStake' | 'sameStake' = betslip.stakeType === 3 ? 'differentStake' : 'sameStake';

          if (betslip.stakeType === 1){
            stakeSelection ='totalStake';
            dispatch.betslip.setSingleStake({ key: 'totalStake', value: betslip.totalStake / 100 });
          } else if (betslip.stakeType === 2){
            dispatch.betslip.setSingleStake({ key: 'sameStake', value: betslip.stake / 100 });
          }

          dispatch.betslip.changeStakeSelection(stakeSelection);
        }

        if (betslip.betType === 2){
          dispatch.betslip.setAccaStake({ key: 'totalStake', value: betslip.totalStake / 100 });
          dispatch.betslip.changeStakeSelection('totalStake');
        }

        if (betslip.betType === 3){
          const bankersArray: boolean[] = [];
          const bankersUpdate = [];
          const selectedLines = [];
          await dispatch.betslip.calculateBets(true);
          const stakes: number [] = [];
          let isStakePerSystem = betslip.stakeType === 4;
          const stakePerSystem: number[] = [];
          dispatch.betslip.addMultipleSelectedCombinations(betslip.systems);

          if (betslip.stakeType === 1)  {
            dispatch.betslip.setSystemStake({ key: 'totalStake', value: betslip.totalStake / 100 });
            await dispatch.betslip.changeStakeSelection('totalStake');
          }

          let bankerIndex = 0;
          for (const receivedBets of betslip.bets){
            bankersArray.push(receivedBets['isBanker']);
            bankersUpdate.push({ index: bankerIndex, isBanker: receivedBets['isBanker'] });
            bankerIndex+=1;
          }

          for (const index in betslip.stakes.combinations){
            if (betslip.stakes.combinations[index].stake > 0 && !selectedLines.includes(betslip.stakes.combinations[index].lines.length)){
              selectedLines.push(betslip.stakes.combinations[index].lines.length);
            }
            stakes.push(betslip.stakes.combinations[index].stake);

            if (+index === 0){
              stakePerSystem[betslip.stakes.combinations[index].lines.length -1] = betslip.stakes.combinations[index].stake;
            }

            if (+index > 0 &&
                betslip.stakes.combinations[+index].lines.length === betslip.stakes.combinations[+index -1].lines.length &&
                betslip.stakes.combinations[index].stake !== betslip.stakes.combinations[+index -1].stake
            ){
              isStakePerSystem = false;
            }

            if (+index < betslip.stakes.combinations.length - 1 &&
                 betslip.stakes.combinations[+index].lines.length !== betslip.stakes.combinations[+index +1].lines.length && isStakePerSystem){
              stakePerSystem[betslip.stakes.combinations[index].lines.length -1] = betslip.stakes.combinations[index].stake;
            }
          }

          if (!isStakePerSystem && betslip.stakeType !== 1){
            const payload: {
            systemStakes: BetslipState['stakes'],
            selectedCombinations: BetslipState['systemSlip']['selectedCombinations'],
            stakes: BetslipState['systemSlip']['stakes'],
            bankers: BetslipState['systemSlip']['bankers'],
            stakeSelection: BetslipState['systemSlip']['stakeSelection']}  = {
              stakes,
              systemStakes: betslip.stakes.combinations,
              selectedCombinations: betslip.systems,
              bankers: bankersArray,
              stakeSelection: 'differentStake'
            };
            await dispatch.betslip.setSystemStake({ key: 'totalStake', value: betslip.totalStake / 100 });

            await dispatch.betslip.changeMultipleSystemRegister(payload);
            await dispatch.betslip.calculateBets(false);
          } else if (isStakePerSystem){
            const stakesArray: number[] = Array.from(stakePerSystem, item => item || null);

            await dispatch.betslip.updateAllSystemStakes(newStakeCombinations);
            await dispatch.betslip.addMultipleSelectedCombinations(betslip.systems);
            await dispatch.betslip.changeStakeSelection('stakePerSystem');
            await dispatch.betslip.changeStakePerSystem(stakesArray);
          }
          if (isStakePerSystem){
            for (const banker of bankersUpdate){
              dispatch.betslip.updateBankerAtIndex(banker);
            }
            dispatch.betslip.setProcessing(false);
          }
        }
      } catch (error) {
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
        dispatch.betslip.setProcessing(false);
      }
    },

    async bookBet(payload:number):Promise<void> {
      try {
        const response = await await get(replaceUrls(apiUrls.getBet, { betId: payload }));
        dispatch.betslip.setProcessing(true);
        const betslip : {
        stakes: any,
        betType: number,
        stakeType: number,
        totalStake: number,
        stake: number,
        bets: [],
        systems: number[]
      }= response.data.results;

        dispatch.betslip.operatBookBetToBetslip(betslip);

      } catch (error) {
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
        dispatch.betslip.setProcessing(false);
      }
    },

    async placeBookedBet(payload, state):Promise<void> {
      const { auth: { localWalletSelected, tillUser } } = state;
      const selectedWallet = localWalletSelected ? localWalletSelected : null;

      const bet = await get(replaceUrls(apiUrls.getPlaceBet, { betId: payload }));

      if (bet && bet.status === 200){
        const response = await post(apiUrls.submitBets, {
          ...bet.data.results.ticket,
          ...(selectedWallet && { customerWallet: selectedWallet.id })
        });
        if (response) {
          if (response.status === 200) {
            dispatch.toast.showToast({ type: 'success', message: 'Bet accepted, view details in history' });

            dispatch.betslip.setProcessing(false);
            if (tillUser){
              dispatch.peripherals.submitToPrinter(response.data.results);
              await dispatch.peripherals.getTillTransactions();
              await dispatch.peripherals.getTillBalance();
            }

          } else if (response.status === 409) {
            const message = checkError(response.data.results);
            dispatch.toast.showToast({ type: 'error', message });
            dispatch.betslip.setProcessing(false);
            dispatch.betslip.setProcessing(false);

            // TODO: remove comment when backend sends different error codes.
            // return { redirect: '/account/deposit' };
          } else {
            const message = checkError(response.data.results);
            dispatch.toast.showToast({ type: 'error', message });
            dispatch.betslip.setProcessing(false);
            dispatch.betslip.setProcessing(false);
          }
        }
      }
    },

    reuseLastSelections(_, state):void {
      const newBetslip = JSON.parse(JSON.stringify(state.betslip.tempBets));

      if (newBetslip.bets && newBetslip.bets.length > 0){
        dispatch.betslip.applySlipState();
      }
    },

    changeIsGetTicket(payload):void {
      dispatch.betslip.setGetTicket(payload);
    },

    changeShowProfit(){
      dispatch.betslip.setShowProfit();
    },

    changeReadShowPermutations(payload:boolean){
      dispatch.betslip.setShowReadPermutations(payload);
    },

    changeReadOpenCombinations(payload){
      dispatch.betslip.updateReadOpenCombinations(payload);
    },

    changeReadBetPermutations(payload){
      dispatch.betslip.updateReadBetPermutations(payload);
    },

    async changeBetsipOptions(payload) {
      dispatch.betslip.setBetslipOptions(payload);
      await dispatch.betslip.calculateBets(false);
    }
  }),

  selectors: (slice, createSelector) => ({
    /**
     * Creates a combinations matrix with X for existing combination.
     */
    combinationsMatrix() {
      return slice(state => {
        return _.reduce(state.stakes, (matrix, combination) => {
          const c = JSON.parse(JSON.stringify(combination));

          const outcomes = Array(state.bets.length).fill('');
          for (const line of c.lines) {
            outcomes[line - 1] = 'X';
          }

          matrix[c.lines.toString()] = { outcomes, odds: c.odds };

          return matrix;
        }, {});
      });
    },

    /**
     * Takes the combinations matrix and returns the 'Any X of Y' list with the combinations info.
     */
    combinations() {
      return createSelector(
        slice,
        this.combinationsMatrix,
        ({ bets, stakes, systemSlip: { systemMaximum, systemMinimum, bankersCount }, slip }, combinationsMatrix) => {
          const combinations: Record<string, any[]> = {};
          if (slip === 2){
            for (let i=systemMinimum; i<= systemMaximum; i+=1){

              let name = `${ i } of ${ systemMaximum } `;

              if (bankersCount > 0){
                name += `+ ${ bankersCount }`;
              }
              if (!combinations[name]) {
                combinations[name] = [];
              }

              for (const combination of stakes) {
                if (combination['lines'].length - bankersCount === i) {
                  combinations[name].push({
                    key: combination.lines.join(','),
                    odds: combination.odds,
                    stake: combination.stake * combination.betsCount,
                    winnings: combination.winnings
                  });
                }
              }
            }
          } else {
            for (const key of Object.keys(combinationsMatrix)) {
            // Default name
              const name = `${ _.filter(combinationsMatrix[key].outcomes, o => o === 'X').length } of ${ bets.length }`;
              if (!combinations[name]) {
                combinations[name] = [];
              }
              for (const combination of stakes) {
                if (key === combination.lines.toString()) {
                  combinations[name].push({
                    key,
                    odds: combination.odds,
                    stake: combination.stake * combination.betsCount,
                    winnings: combination.winnings
                  });
                }
              }
            }
          }

          return combinations;
        }
      );
    },

    /**
     * Returns number of existing combinable bets.
     */
    noCombinableBets() {
      return slice(state => {
        const combinable = [];

        if (state.bets) {
          for (const bet of state.bets) {
            if (bet.combinationExclusions && bet.combinationExclusions.length > 0) {
              combinable.push(bet.combinationExclusions.length + 1 >= state.bets.length);
            } else {
              combinable.push(false);
            }
          }
        }

        return _.filter(combinable, c => c === true).length === combinable.length;
      });
    },

    /**
     * Returns the sum of combinations or '-' if there's no local combinations.
     */
    totalCombinations() {
      return slice(state => {
        return state.stakes.length > 0 ? _.reduce(state.stakes, (total, s) => total += s.betsCount, 0) : '-';
      });
    },

    /**
     * Returns the selected slip' staking.
     */
    selectedSlip() {
      return slice(({ slip, singleSlip, accaSlip, systemSlip }) => {
        let selectedSlip = singleSlip;
        if (slip === 1) selectedSlip = accaSlip;
        else if (slip === 2) selectedSlip = systemSlip;

        return selectedSlip;
      });
    },

    /**
     * Returns total amount to be paid.
     */
    totalAmount() {
      return slice(({ bets, slip, singleSlip, accaSlip, systemSlip, divisor }) => {
        let value = 0;

        switch (slip) {
          case 0:
            if (singleSlip.stakeSelection === 'differentStake') {
              value = _.reduce(singleSlip.stakes, (total, s) => total += s, 0) / (divisor || 100);
            } else if (singleSlip.stakeSelection === 'sameStake') {
              value = (singleSlip.sameStake * bets.length) / (divisor || 100);
            } else {
              value = singleSlip[singleSlip.stakeSelection] / (divisor || 100);
            }
            break;

          case 1:
            if (accaSlip.stakeSelection === 'sameStake') {
              value = (accaSlip.sameStake * accaSlip.stakes.length) / (divisor || 100);
            } else {
              value = accaSlip[accaSlip.stakeSelection] / (divisor || 100);
            }
            break;

          case 2:
            if (systemSlip.stakeSelection === 'differentStake' || systemSlip.stakeSelection === 'sameStake') {
              value = _.reduce(systemSlip.stakes, (total, s) => total += s, 0) / (divisor || 100);
            } else if (systemSlip.stakeSelection === 'stakePerSystem'){
              value = _.reduce(systemSlip.stakePerSystem, (total, s) => total += s, 0) / (divisor || 100);
            } else {
              value = systemSlip[systemSlip.stakeSelection] / (divisor || 100);
            }
            break;
        }

        return value;
      });
    }
  })
});
